import { Temporal } from "@/Temporal"
import AutoColumns from "@/components/AutoColumns"
import BasicSelect from "@/components/form/BasicSelect"
import { EmergencyPhase, IncidentStatus, SystemRole, User, type Id } from "@/models"
import { useCoreData, useUser } from "@/stores/coreDataStore"
import { formatEnum } from "@/utils/formatEnum"
import { computed, onMounted } from "vue"
import { Required, defineComponent, refSetter, type ReactiveComponent } from "vue-utils"
import { useIncidentHandler } from "../IncidentLogHandler"
import IncidentInput from "../components/IncidentInput"
import IncidentInstantInput from "../components/IncidentInstantInput"
import IncidentTextArea from "../components/IncidentTextArea"
import { useIncidentSelectRef } from "../inputHelpers"

const MainDetails: ReactiveComponent = () => {
	const handler = useIncidentHandler()
	const coreData = useCoreData()

	const allUsers = computed(() => Array.from(coreData.users.values()))
	const smcUsers = computed(() => allUsers.value.filter((u) => u.role === SystemRole.SMC))
	const createdBy = useUser(() => handler.incident.createdBy.userId)
	const canEdit = () => !handler.readOnly && handler.isNewIncident

	const changeBuilder = handler.changeBuilder

	const smcRef = useIncidentSelectRef<Id | null>(changeBuilder.at("SMCOnDutyId"))
	const primaryMwoRef = useIncidentSelectRef<Id | null>(changeBuilder.at("primaryMWOId"))
	const secondaryMwoRef = useIncidentSelectRef<Id | null>(changeBuilder.at("secondaryMWOId"))
	const reportedIncidentTypeRef = useIncidentSelectRef(changeBuilder.at("reportedIncidentTypeId"))
	const finalIncidentTypeRef = useIncidentSelectRef(changeBuilder.at("finalIncidentTypeId"))
	const initialEmergencyPhaseRef = useIncidentSelectRef(changeBuilder.at("initialEmergencyPhase"))
	const finalEmergencyPhaseRef = useIncidentSelectRef(changeBuilder.at("finalEmergencyPhase"))

	onMounted(() => {
		if (!canEdit() || !createdBy.value) return
		primaryMwoRef.value = createdBy.value.id
	})

	return () => (
		<div class="flex flex-col spacing-4">
			<AutoColumns size="16rem" class="spacing-4">
				{!handler.isNewIncident && (
					<label>
						Incident Identifier
						<input type="text" value={handler.incident.identifier} disabled />
					</label>
				)}
				<label>
					<Required label="Incident Time (UTC)" />
					<IncidentInstantInput
						path={changeBuilder.at("incidentTime")}
						max={Temporal.Now.instant()}
						disabled={!canEdit()}
						required
					/>
				</label>
				<label>
					Opened By
					<input type="text" value={createdBy.value?.fullName ?? "-"} disabled />
				</label>
				<label>
					Opened At (UTC)
					<input type="datetime-local" value={handler.incident.createdBy.timeStamp.toHTMLInputUTC()} disabled />
				</label>
				<label>
					Status
					<BasicSelect
						options={Object.values(IncidentStatus)}
						value={handler.incident.status}
						setValue={() => void 0}
						getText={formatEnum}
						getValue={(s) => s}
						required
						disabled
					/>
				</label>
			</AutoColumns>
			<AutoColumns size="16rem" class="spacing-4">
				<label>
					<Required label="Reported Incident Type" />
					<BasicSelect
						options={coreData.incidentTypes.toSorted((a, b) => a.name.localeCompare(b.name))}
						value={reportedIncidentTypeRef.value}
						setValue={(type) => (reportedIncidentTypeRef.value = type.id)}
						defaultText={!canEdit() ? "N/A" : "Select Incident Type"}
						disabled={!canEdit()}
						required
					/>
				</label>

				<label>
					Final Incident Type
					<BasicSelect
						options={coreData.incidentTypes.toSorted((a, b) => a.name.localeCompare(b.name))}
						value={finalIncidentTypeRef.value}
						setValue={(type) => {
							finalIncidentTypeRef.value = type?.id ?? null
						}}
						defaultText="N/A"
						disabled={handler.readOnly}
					/>
				</label>

				<label>
					<Required label="Initial Emergency Phase" />
					<BasicSelect
						options={Object.values(EmergencyPhase)}
						value={initialEmergencyPhaseRef.value}
						setValue={refSetter(initialEmergencyPhaseRef)}
						defaultText={!canEdit() ? "N/A" : "Select Emergency Phase"}
						getText={formatEnum}
						disabled={!canEdit()}
						required
					/>
				</label>

				<label>
					Final Emergency Phase
					<BasicSelect
						options={Object.values(EmergencyPhase)}
						value={finalEmergencyPhaseRef.value}
						setValue={refSetter(finalEmergencyPhaseRef)}
						defaultText="N/A"
						getText={formatEnum}
						disabled={handler.readOnly}
					/>
				</label>
			</AutoColumns>
			<AutoColumns size="16rem" class="spacing-4">
				<label>
					{smcUsers.value.length > 0 ? <Required label="SMC on Duty" /> : "SMC on Duty"}
					<BasicSelect
						options={smcUsers.value}
						getText={(u) => u.emailAddress}
						getValue={(u) => String(u.id)}
						value={smcRef.value}
						setValue={(u: User | null) => (smcRef.value = u?.id ?? null)}
						defaultText="N/A"
						disabled={!canEdit()}
						required={smcUsers.value.length > 0}
					/>
				</label>
				<label>
					Primary MWO
					<BasicSelect
						options={allUsers.value}
						getText={(u) => u.emailAddress}
						getValue={(u) => String(u.id)}
						value={primaryMwoRef.value}
						setValue={(u: User | null) => (primaryMwoRef.value = u?.id ?? null)}
						defaultText="N/A"
						disabled={!canEdit()}
					/>
				</label>
				<label>
					Secondary MWO
					<BasicSelect
						options={allUsers.value}
						getText={(u) => u.emailAddress}
						getValue={(u) => String(u.id)}
						value={secondaryMwoRef.value}
						setValue={(u: User | null) => (secondaryMwoRef.value = u?.id ?? null)}
						defaultText="N/A"
						disabled={handler.readOnly}
					/>
				</label>
				<label>
					<Required label="Persons At Risk" />
					<IncidentInput
						type="number"
						path={changeBuilder.at("personsAtRisk")}
						min={0}
						step={1}
						disabled={!canEdit()}
						required
					/>
				</label>

				<label>
					Name of Vessel or Casualty
					<IncidentInput
						type="text"
						path={changeBuilder.at("nameOfVesselOrCasualty")}
						placeholder={!canEdit() ? "N/A" : "Enter name..."}
						disabled={!canEdit()}
					/>
				</label>
			</AutoColumns>

			<label class="flex">
				<Required label="Message Received" />
				<IncidentTextArea
					path={changeBuilder.at("messageReceived")}
					placeholder={!canEdit() ? "N/A" : "Enter the message received..."}
					rows={3}
					disabled={!canEdit()}
					required
				/>
			</label>
			<label>
				Nature of Incident
				<IncidentTextArea
					path={changeBuilder.at("natureOfIncident")}
					placeholder={!canEdit() ? "N/A" : "Enter the nature of the incident..."}
					rows={3}
					disabled={!canEdit()}
				/>
			</label>
			<label>
				Description of Casualty (if applicable)
				<IncidentTextArea
					path={changeBuilder.at("descriptionOfCasualty")}
					placeholder={!canEdit() ? "N/A" : "Enter the description of the casualty..."}
					rows={2}
					disabled={!canEdit()}
				/>
			</label>
		</div>
	)
}

export default defineComponent(MainDetails)
